import { Component, inject, Input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import { OrganizationService } from '../../services/organization.service';
import { AlertService } from '../../services/alert.service';
import { ConfirmDialogService } from '../../services/confirm-dialog.service';
import { OrganizationMembership } from '../../model/organization-membership';
import { ApiResponse } from '../../model/api-response';
import { Organization } from '../../model/organization';
import { PermissionService } from '../../../core/services/permission.service';
import { Messages } from '../../constants/messages';
import { OrgCapability } from '../../model/permissions';

/**
 * Gestione membri di un'organizzazione (lista, aggiunta via email esatta, cambio ruolo, rimozione).
 * Usata solo nella pagina di edit organizzazione.
 *
 * I controlli senza permesso restano visibili ma disabilitati.
 * Le mutazioni restano soggette al backend: in caso di 403 l'interceptor mostra l'errore.
 */
@Component({
  selector: 'app-organization-members-manager',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, NgClass],
  templateUrl: './organization-members-manager.component.html',
  styleUrl: './organization-members-manager.component.css',
})
export class OrganizationMembersManagerComponent implements OnInit, OnChanges {
  @Input({ required: true }) organization!: Organization;

  protected members = signal<OrganizationMembership[]>([]);
  protected isLoading = signal<boolean>(false);
  protected isAdding = signal<boolean>(false);
  protected emailControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.email],
  });
  protected newRole = 'MEMBER';
  protected roles: string[] = ['MEMBER', 'MODERATOR', 'ADMIN', 'OWNER'];
  protected readonly noPermissionTitle = 'Non hai i permessi per questa azione';

  private organizationService: OrganizationService = inject(OrganizationService);
  private alertService: AlertService = inject(AlertService);
  private confirmDialogService: ConfirmDialogService = inject(ConfirmDialogService);
  private permissionService: PermissionService = inject(PermissionService);

  ngOnInit(): void {
    if (this.organization?.id) {
      this.loadMembers();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organization'] && !changes['organization'].firstChange) {
      this.loadMembers();
    }
  }

  loadMembers(): void {
    this.isLoading.set(true);
    this.organizationService.getOrganizationMembers(this.organization.id).subscribe({
      next: (response: ApiResponse<OrganizationMembership[]>) => {
        if (response.success && response.data) {
          this.members.set(response.data);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      },
    });
  }

  canInvite(): boolean {
    return this.permissionService.hasOrgCapability(
      this.organization,
      OrgCapability.ORG_MEMBER_INVITE,
    );
  }

  canAssignNewRole(role: string): boolean {
    if (role?.toUpperCase() === 'MEMBER') {
      return this.canInvite();
    }
    return this.canAssign(role);
  }

  canAssign(role: string): boolean {
    return this.permissionService.canAssignRole(this.organization, role);
  }

  canUpdateRole(member: OrganizationMembership): boolean {
    return (
      this.permissionService.hasOrgCapability(this.organization, OrgCapability.ORG_ROLE_ASSIGN) &&
      this.permissionService.canManageMember(this.organization, member.organizationRole)
    );
  }

  canRemove(member: OrganizationMembership): boolean {
    return this.permissionService.canManageMember(this.organization, member.organizationRole);
  }

  addMember(): void {
    if (this.emailControl.invalid || !this.canInvite() || !this.canAssignNewRole(this.newRole)) {
      this.emailControl.markAsTouched();
      return;
    }
    const email = this.emailControl.value.trim();
    if (!email) {
      return;
    }
    this.isAdding.set(true);
    this.organizationService
      .addOrganizationMember(this.organization.id, email, this.newRole)
      .subscribe({
        next: (response: ApiResponse<OrganizationMembership>) => {
          this.isAdding.set(false);
          if (response.success) {
            this.alertService.showSuccess(`Utente con email "${email}" aggiunto come membro`);
            this.emailControl.reset('');
            this.newRole = 'MEMBER';
            this.loadMembers();
          }
        },
        error: () => {
          this.isAdding.set(false);
        },
      });
  }

  updateRole(member: OrganizationMembership, role: string): void {
    if (member.organizationRole === role || !this.canUpdateRole(member) || !this.canAssign(role)) {
      return;
    }
    this.organizationService
      .updateOrganizationMemberRole(this.organization.id, member.userId, role)
      .subscribe({
        next: (response: ApiResponse<OrganizationMembership>) => {
          if (response.success) {
            this.alertService.showSuccess('Ruolo aggiornato con successo');
            this.loadMembers();
          }
        },
      });
  }

  async removeMember(member: OrganizationMembership): Promise<void> {
    if (!this.canRemove(member)) {
      return;
    }
    const accepted = await this.confirmDialogService.confirm({
      title: Messages.CONFIRM_MEMBER_REMOVE_TITLE,
      message: Messages.confirmMemberRemove(member.userName, member.userId),
      confirmLabel: Messages.CONFIRM_REMOVE,
    });
    if (!accepted) {
      return;
    }
    this.organizationService
      .removeOrganizationMember(this.organization.id, member.userId)
      .subscribe({
        next: (response: ApiResponse<string>) => {
          if (response.success) {
            this.alertService.showSuccess('Membro rimosso con successo');
            this.loadMembers();
          }
        },
      });
  }

  getRoleBadgeClass(role: string): string {
    switch (role?.toUpperCase()) {
      case 'OWNER':
        return 'bg-success';
      case 'ADMIN':
        return 'bg-primary';
      case 'MODERATOR':
        return 'bg-warning';
      default:
        return 'bg-info';
    }
  }
}
